import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Wrench, ShieldAlert, Users, Gauge, History, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import EarlyAccessRequestsPanel from "@/pages/components/admin/EarlyAccessRequestsPanel";
import { ExtendEndCard, EXTENSION_MODE_LABELS } from "@/components/ExtendEndCard";
import { ExtensionsList } from "@/components/ExtensionsList";

// Iter456 · Festival Toolkit — scoped goodwill overrides.
// Admins see the three override controls + full audit log. The
// festival owner (non-admin) sees ONLY the audit log with the
// `actor_email` / `actor_name` columns masked server-side.

const ACTION_LABELS = {
  toolkit_tier_override: "Tier override",
  toolkit_extra_helpers: "Extra helpers",
  toolkit_player_cap_increase: "Player cap increase",
  toolkit_end_extended: "End time extended",
  toolkit_extension_link_issued: "Extension link issued",
  toolkit_extension_link_resent: "Extension link resent",
  toolkit_extension_payment_received: "Extension paid",
  toolkit_extension_cancelled: "Extension cancelled",
};

// Iter457 · Fix 2 — Role keys mirror backend `HELPER_ROLE_KEYS` in
// routes_festival_toolkit.py. Keeping it mirrored (rather than
// relying on the /state response's `helper_role_keys`) means the UI
// always renders all five rows, even on a pristine festival where
// the override dict is empty.
const HELPER_ROLE_KEYS = [
  "check_in_only",
  "court_operations",
  "ops_assist",
  "deputy",
];
const HELPER_ROLE_LABELS = {
  check_in_only: "Check-In Only",
  court_operations: "Court Operations",
  ops_assist: "OPS Assist",
  deputy: "Deputy",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

function fmtHelpersSummary(m) {
  // Legacy audit rows may hold a scalar `extra_helpers` or the
  // pre-Iter458 five-role dict; render whatever we're given so the
  // history stays readable after the key rename to the four
  // canonical Toolkit roles.
  if (typeof m === "number") return `+${m} (all roles)`;
  if (!m || typeof m !== "object") return "—";
  const parts = Object.entries(m)
    .filter(([, v]) => Number(v) > 0)
    .map(([k, v]) => {
      const label = HELPER_ROLE_LABELS[k] || k;
      // Shorten "Check-In Only" → "Check-In", "Court Operations" →
      // "Court", "OPS Assist" → "OPS", "Deputy" → "Deputy" so the
      // audit cell stays compact.
      const short = label === "Check-In Only" ? "Check-In"
        : label === "Court Operations" ? "Court"
        : label === "OPS Assist" ? "OPS"
        : label === "Deputy" ? "Deputy"
        : label.split(" ")[0];
      return `${short}: +${v}`;
    });
  return parts.length ? parts.join(", ") : "none";
}

function fmtDelta(action, before = {}, after = {}) {
  if (action === "toolkit_tier_override") {
    return `${before.tier || "—"} → ${after.tier || "—"}`;
  }
  if (action === "toolkit_extra_helpers") {
    return `${fmtHelpersSummary(before.extra_helpers)} → ${fmtHelpersSummary(after.extra_helpers)}`;
  }
  if (action === "toolkit_end_extended") {
    const extra = after.amount_invoiced ? ` · £${Number(after.amount_invoiced).toFixed(2)} invoiced`
      : after.amount_paid ? ` · £${Number(after.amount_paid).toFixed(2)} paid` : after.status === "complimentary" ? " · complimentary" : "";
    return `${fmtDate(before.end_at)} → ${fmtDate(after.end_at)}${extra}`;
  }
  if (action === "toolkit_extension_link_issued" || action === "toolkit_extension_link_resent" || action === "toolkit_extension_cancelled") {
    return `→ ${fmtDate(after.requested_end_at)} · £${Number(after.amount_invoiced || 0).toFixed(2)}`;
  }
  if (action === "toolkit_extension_payment_received") {
    return `£${Number(after.amount_paid || 0).toFixed(2)} paid ${fmtDate(after.paid_at)}`;
  }
  if (action === "toolkit_player_cap_increase") {
    return `+${before.extra_player_cap || 0} → +${after.extra_player_cap || 0}`;
  }
  return "";
}

export default function FestivalToolkit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [log, setLog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmFor, setConfirmFor] = useState(null); // { kind, payload }

  const load = async () => {
    try {
      const [s, l] = await Promise.all([
        api.get(`/festival/${id}/toolkit/state`),
        api.get(`/festival/${id}/toolkit/audit-log`),
      ]);
      setState(s.data);
      setLog(l.data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Toolkit");
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!state) return <div className="p-8 text-sm text-[#A3A3A3]">Loading…</div>;

  const isAdmin = !!state.is_admin;
  const current = state.current || {};
  const overrides = state.overrides || {};
  const ladder = state.ladder || [];

  const submit = async (payload) => {
    setBusy(true);
    try {
      await api.post(`/festival/${id}/toolkit/${payload.endpoint}`, payload.body);
      toast.success(payload.successMsg);
      setConfirmFor(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="festival-toolkit">
      <div className="flex items-center gap-3 mb-4">
        <button
          type="button"
          onClick={() => navigate(`/festival/${id}`)}
          data-testid="toolkit-back"
          className="text-[11px] uppercase tracking-wider text-[#A3A3A3] hover:text-white inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Festival
        </button>
      </div>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <div className="label-mono text-[#CCFF00] text-[10px] mb-1">
            {isAdmin ? "FESTIVAL · TOOLKIT" : "FESTIVAL · ACTIVITY LOG"}
          </div>
          <h1 className="h-display text-3xl inline-flex items-center gap-2">
            <Wrench className="w-6 h-6 text-[#CCFF00]" />
            {/* Iter457 · Fix 4 — Organiser view surfaces this page
                 as an audit / activity surface, not an override
                 tool. The admin controls below are gated on
                 `isAdmin` anyway; the heading just matches the
                 nav label the organiser clicked through. */}
            {isAdmin ? "Overrides" : "Festival Activity Log"}
          </h1>
          <p className="text-sm text-[#A3A3A3] mt-1 max-w-xl">
            {isAdmin
              ? <>Goodwill tweaks scoped to <span className="text-white">this festival only</span>. Nothing here changes your account's default tier or limits for future festivals.</>
              : <>Every override Rally-Lab applies to this festival shows up here. Admin names are hidden; actions and reasons are shown so you can trace what changed.</>}
          </p>
        </div>
        <div className="text-right text-[11px] text-[#A3A3A3]">
          Signed in as <span className="text-white">{user?.email || "—"}</span><br />
          {isAdmin ? (
            <span className="text-[#CCFF00] uppercase tracking-widest">Rally-Lab admin</span>
          ) : (
            <span className="text-[#FFB020] uppercase tracking-widest">Organiser (read-only)</span>
          )}
        </div>
      </div>

      {/* Current state card */}
      <div className="card-surface p-4 mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="toolkit-current">
        <div>
          <div className="label-mono text-[#A3A3A3] text-[10px]">CURRENT TIER</div>
          <div className="text-lg font-bold mt-1" data-testid="toolkit-current-tier">{current.label || "—"}</div>
          <div className="text-[11px] text-[#525252]">{current.days ? `${current.days}-day` : ""}</div>
        </div>
        <div>
          <div className="label-mono text-[#A3A3A3] text-[10px]">PLAYER CAP</div>
          <div className="text-lg font-bold mt-1" data-testid="toolkit-current-cap">
            {(current.player_cap ?? "∞")}
            {overrides.extra_player_cap ? (
              <span className="ml-2 text-[#CCFF00] text-sm" data-testid="toolkit-cap-override-chip">
                +{overrides.extra_player_cap} override
              </span>
            ) : null}
          </div>
        </div>
        <div>
          <div className="label-mono text-[#A3A3A3] text-[10px]">HELPER SEATS</div>
          <div className="text-lg font-bold mt-1" data-testid="toolkit-current-helpers">
            {(current.helper_seats ?? "∞")}
            {(() => {
              const h = overrides.extra_helpers || {};
              const total = typeof h === "number" ? h : HELPER_ROLE_KEYS.reduce((s, k) => s + (Number(h[k]) || 0), 0);
              return total > 0 ? (
                <span className="ml-2 text-[#CCFF00] text-sm" data-testid="toolkit-helpers-override-chip">
                  +{total} override
                </span>
              ) : null;
            })()}
          </div>
        </div>
      </div>

      {/* Admin-only override controls */}
      {isAdmin && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          <TierOverrideCard
            current={current}
            ladder={ladder}
            overrides={overrides}
            onSubmit={(pass_type, reason) => setConfirmFor({
              kind: "tier",
              headline: "Confirm tier override",
              body: `Bump the active tier to ${ladder.find((p) => p.pass_type === pass_type)?.label || pass_type} for this festival only.`,
              endpoint: "tier-override",
              payload: { pass_type, reason },
              successMsg: "Tier override applied",
            })}
          />
          <ExtraHelpersCard
            overrides={overrides}
            onSubmit={(extras, reason) => {
              const total = HELPER_ROLE_KEYS.reduce((s, k) => s + (extras[k] || 0), 0);
              setConfirmFor({
                kind: "helpers",
                headline: "Confirm extra helper slots",
                body: `Grant extra helper slots across ${HELPER_ROLE_KEYS.filter((k) => (extras[k] || 0) > 0).length} role${total === 1 ? "" : "s"} above the tier default (total +${total}).`,
                endpoint: "extra-helpers",
                payload: { extras, reason },
                successMsg: "Extra helpers applied",
              });
            }}
          />
          <PlayerCapCard
            overrides={overrides}
            onSubmit={(extra_cap, reason) => setConfirmFor({
              kind: "cap",
              headline: "Confirm player cap increase",
              body: `Raise this festival's player cap by ${extra_cap} above the tier default.`,
              endpoint: "player-cap-increase",
              payload: { extra_cap, reason },
              successMsg: "Player cap raised",
            })}
          />
        </div>
      )}

      {isAdmin && <EarlyAccessRequestsPanel festivalId={id} />}

      {isAdmin && (
        <ExtendEndCard
          key={state.festival?.end_at || "none"}
          festival={state.festival || {}}
          onSubmit={(p) => setConfirmFor({
            kind: "extend_end",
            headline: `Confirm: ${EXTENSION_MODE_LABELS[p.mode]}`,
            body: p.mode === "pay_later"
              ? `Move the festival end from ${fmtDate(state.festival?.end_at)} to ${fmtDate(p.new_end_at)} now, and email the organiser a £${p.amount.toFixed(2)} Stripe payment link.`
              : p.mode === "on_payment"
              ? `Email the organiser a £${p.amount.toFixed(2)} Stripe payment link. The end moves to ${fmtDate(p.new_end_at)} only once payment is confirmed.`
              : `Move the festival end from ${fmtDate(state.festival?.end_at)} to ${fmtDate(p.new_end_at)} now at no charge${p.note ? ` — "${p.note}"` : ""}.`,
            endpoint: "extensions",
            payload: { ...p, origin_url: window.location.origin },
            successMsg: p.mode === "on_payment" ? "Payment link emailed — extension activates on payment" : "Festival end extended — organiser notified",
          })}
        />
      )}

      {isAdmin && <ExtensionsList festivalId={id} reloadToken={state} onChanged={load} />}

      {/* Audit log */}
      <div className="card-surface overflow-hidden" data-testid="toolkit-audit">
        <div className="p-4 border-b border-[#262626] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-[#CCFF00]" />
            <div className="label-mono text-[10px] text-white">AUDIT LOG</div>
          </div>
          <div className="text-[10px] text-[#525252]">
            {isAdmin ? "Full detail · admin view" : "Organiser view · admin name hidden"}
          </div>
        </div>
        {(log || []).length === 0 ? (
          <div className="p-8 text-center text-sm text-[#525252]" data-testid="toolkit-audit-empty">
            No overrides have been applied to this festival yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-[#525252] border-b border-[#262626]">
                  <th className="text-left p-3">When</th>
                  <th className="text-left p-3">Action</th>
                  <th className="text-left p-3">Change</th>
                  <th className="text-left p-3">Reason</th>
                  {isAdmin && <th className="text-left p-3">Admin</th>}
                </tr>
              </thead>
              <tbody>
                {log.map((row) => (
                  <tr key={row.id} className="border-b border-[#141414] hover:bg-[#0F0F0F]" data-testid={`toolkit-audit-row-${row.id}`}>
                    <td className="p-3 text-xs text-[#A3A3A3] whitespace-nowrap">{fmtDate(row.created_at)}</td>
                    <td className="p-3 text-xs">
                      <span className="inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider border border-[#CCFF00] text-[#CCFF00]">
                        {ACTION_LABELS[row.action] || row.action}
                      </span>
                    </td>
                    <td className="p-3 text-xs text-white font-mono">{fmtDelta(row.action, row.before, row.after)}</td>
                    <td className="p-3 text-xs text-[#E0E0E0] max-w-md" data-testid={`toolkit-audit-reason-${row.id}`}>
                      {row.note || "—"}
                    </td>
                    {isAdmin && (
                      <td className="p-3 text-xs text-[#A3A3A3]" data-testid={`toolkit-audit-admin-${row.id}`}>
                        {row.actor_name || row.actor_email || "—"}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirmation modal */}
      {confirmFor && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/85 p-4"
          data-testid="toolkit-confirm-modal"
          onClick={() => !busy && setConfirmFor(null)}
        >
          <div
            className="w-full max-w-md bg-[#0A0A0A] border-2 border-[#CCFF00] p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-3">
              <ShieldAlert className="w-5 h-5 text-[#CCFF00] shrink-0 mt-0.5" />
              <div>
                <div className="label-mono text-[#CCFF00] text-[10px] mb-1">GOODWILL OVERRIDE</div>
                <h2 className="h-display text-xl leading-snug" data-testid="toolkit-confirm-title">
                  {confirmFor.headline}
                </h2>
                <p className="text-xs text-[#A3A3A3] mt-2">{confirmFor.body}</p>
                <p className="text-[11px] text-[#FFB020] mt-2">
                  This applies to this festival only.{confirmFor.payload.reason && <> Reason: <span className="text-white italic">"{confirmFor.payload.reason}"</span></>}
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button
                type="button"
                disabled={busy}
                onClick={() => setConfirmFor(null)}
                data-testid="toolkit-confirm-cancel"
                className="flex-1 px-4 py-3 text-[11px] uppercase tracking-widest border border-[#525252] text-[#A3A3A3] hover:text-white hover:border-white disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => submit({
                  endpoint: confirmFor.endpoint,
                  body: confirmFor.payload,
                  successMsg: confirmFor.successMsg,
                })}
                data-testid="toolkit-confirm-apply"
                className="flex-1 px-4 py-3 text-[11px] uppercase tracking-widest font-bold border border-[#CCFF00] bg-[#CCFF00] text-black hover:bg-white hover:border-white disabled:opacity-40 disabled:cursor-wait"
              >
                {busy ? "Applying…" : "Apply override"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RequireReason({ reason, setReason, children, inputId }) {
  return (
    <>
      {children}
      <label className="block text-[10px] uppercase tracking-widest text-[#A3A3A3] mt-3 mb-1" htmlFor={inputId}>
        Reason (required)
      </label>
      <textarea
        id={inputId}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={2}
        placeholder="Short note for the audit log"
        className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]"
      />
    </>
  );
}

function TierOverrideCard({ current, ladder, overrides, onSubmit }) {
  const [pick, setPick] = useState("");
  const [reason, setReason] = useState("");
  const disabled = !pick || reason.trim().length < 3;
  const noLadder = ladder.length === 0;
  return (
    <div className="card-surface p-4" data-testid="toolkit-tier-card">
      <div className="flex items-center gap-2 mb-2">
        <Gauge className="w-4 h-4 text-[#CCFF00]" />
        <div className="label-mono text-[10px] text-[#CCFF00]">TIER OVERRIDE</div>
      </div>
      <h3 className="text-base font-bold mb-1">Bump this festival to a higher tier</h3>
      <p className="text-[11px] text-[#A3A3A3] mb-3 leading-relaxed">
        Goodwill upgrade — swaps the active <span className="text-white">{current.label || "(no tier)"}</span> for any higher tier with the same {current.days || 0}-day activation. Future festivals keep their own default.
      </p>
      {overrides.tier_override_pass_type && (
        <div className="text-[10px] text-[#66C2FF] mb-2 inline-flex items-center gap-1" data-testid="toolkit-tier-current-override">
          <CheckCircle2 className="w-3 h-3" /> Current override: {overrides.tier_override_tier}
        </div>
      )}
      <RequireReason reason={reason} setReason={setReason} inputId="toolkit-tier-reason">
        <label className="block text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-1">New tier</label>
        <select
          value={pick}
          onChange={(e) => setPick(e.target.value)}
          data-testid="toolkit-tier-select"
          disabled={noLadder}
          className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00] disabled:opacity-40"
        >
          <option value="">{noLadder ? "Already at top tier" : "Select a higher tier…"}</option>
          {ladder.map((p) => (
            <option key={p.pass_type} value={p.pass_type}>{p.label}</option>
          ))}
        </select>
      </RequireReason>
      <button
        type="button"
        disabled={disabled}
        onClick={() => { onSubmit(pick, reason.trim()); setPick(""); setReason(""); }}
        data-testid="toolkit-tier-submit"
        className="w-full mt-3 px-4 py-2.5 text-[11px] uppercase tracking-widest font-bold border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
      >
        Review override
      </button>
    </div>
  );
}

function ExtraHelpersCard({ overrides, onSubmit }) {
  // Iter457 · Fix 2 — Per-role inputs. One shared reason + one
  // shared submit; validation requires at least one role to have
  // a non-zero value AND a reason ≥3 chars.
  const current = overrides.extra_helpers || {};
  const [values, setValues] = useState(() => {
    const seed = {};
    HELPER_ROLE_KEYS.forEach((k) => {
      const n = typeof current === "number" ? current : Number(current[k] || 0);
      seed[k] = n > 0 ? String(n) : "";
    });
    return seed;
  });
  const [reason, setReason] = useState("");
  const parsed = HELPER_ROLE_KEYS.reduce((m, k) => {
    const n = values[k] === "" ? 0 : Number(values[k]);
    m[k] = Number.isInteger(n) && n >= 0 && n <= 100 ? n : null;
    return m;
  }, {});
  const anyInvalid = HELPER_ROLE_KEYS.some((k) => parsed[k] === null);
  const anyPositive = HELPER_ROLE_KEYS.some((k) => (parsed[k] || 0) > 0);
  const changed = HELPER_ROLE_KEYS.some((k) => {
    const prev = typeof current === "number" ? current : Number(current[k] || 0);
    return (parsed[k] || 0) !== prev;
  });
  const disabled = anyInvalid || !changed || reason.trim().length < 3;
  const currentTotal = typeof current === "number"
    ? current * HELPER_ROLE_KEYS.length
    : HELPER_ROLE_KEYS.reduce((s, k) => s + Number(current[k] || 0), 0);
  return (
    <div className="card-surface p-4" data-testid="toolkit-helpers-card">
      <div className="flex items-center gap-2 mb-2">
        <Users className="w-4 h-4 text-[#CCFF00]" />
        <div className="label-mono text-[10px] text-[#CCFF00]">EXTRA HELPERS</div>
      </div>
      <h3 className="text-base font-bold mb-1">Grant extra helper slots per role</h3>
      <p className="text-[11px] text-[#A3A3A3] mb-3 leading-relaxed">
        Adds to the tier default for this festival only. Set a role to <span className="text-white">0</span> to clear its override, or save without changes to leave it as-is.
      </p>
      <div className="text-[10px] text-[#66C2FF] mb-3 inline-flex items-center gap-1" data-testid="toolkit-helpers-current">
        <CheckCircle2 className="w-3 h-3" /> Currently granted: +{currentTotal} across all roles
      </div>
      <div className="space-y-2 mb-1">
        {HELPER_ROLE_KEYS.map((k) => (
          <div key={k} className="flex items-center gap-2">
            <label className="flex-1 text-[11px] text-[#E0E0E0]" htmlFor={`toolkit-helpers-input-${k}`}>
              {HELPER_ROLE_LABELS[k]}
            </label>
            <input
              id={`toolkit-helpers-input-${k}`}
              type="number"
              min={0}
              max={100}
              value={values[k]}
              onChange={(e) => setValues((v) => ({ ...v, [k]: e.target.value }))}
              data-testid={`toolkit-helpers-input-${k}`}
              placeholder="0"
              className={`w-20 bg-[#0A0A0A] border px-2 py-1 text-sm text-right focus:outline-none ${parsed[k] === null ? "border-[#FF4500]" : "border-[#262626] focus:border-[#CCFF00]"}`}
            />
          </div>
        ))}
      </div>
      <label className="block text-[10px] uppercase tracking-widest text-[#A3A3A3] mt-3 mb-1" htmlFor="toolkit-helpers-reason">
        Reason (required)
      </label>
      <textarea
        id="toolkit-helpers-reason"
        data-testid="toolkit-helpers-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={2}
        placeholder="Short note for the audit log"
        className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]"
      />
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          onSubmit(parsed, reason.trim());
          setReason("");
        }}
        data-testid="toolkit-helpers-submit"
        className="w-full mt-3 px-4 py-2.5 text-[11px] uppercase tracking-widest font-bold border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
        title={!anyPositive && !changed ? "Enter values and a reason" : ""}
      >
        Review override
      </button>
    </div>
  );
}

function PlayerCapCard({ overrides, onSubmit }) {
  const [n, setN] = useState("");
  const [reason, setReason] = useState("");
  const asNum = Number(n);
  const disabled = !Number.isInteger(asNum) || asNum < 0 || asNum > 100000 || reason.trim().length < 3;
  return (
    <div className="card-surface p-4" data-testid="toolkit-cap-card">
      <div className="flex items-center gap-2 mb-2">
        <Users className="w-4 h-4 text-[#CCFF00]" />
        <div className="label-mono text-[10px] text-[#CCFF00]">PLAYER CAP INCREASE</div>
      </div>
      <h3 className="text-base font-bold mb-1">Raise the player cap</h3>
      <p className="text-[11px] text-[#A3A3A3] mb-3 leading-relaxed">
        Adds to the tier default for this festival only. Set to <span className="text-white">0</span> to remove the override.
      </p>
      <div className="text-[10px] text-[#66C2FF] mb-2 inline-flex items-center gap-1" data-testid="toolkit-cap-current">
        <CheckCircle2 className="w-3 h-3" /> Currently granted: +{overrides.extra_player_cap || 0}
      </div>
      <RequireReason reason={reason} setReason={setReason} inputId="toolkit-cap-reason">
        <label className="block text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-1">Extra player slots</label>
        <input
          type="number"
          min={0}
          max={100000}
          value={n}
          onChange={(e) => setN(e.target.value)}
          data-testid="toolkit-cap-input"
          placeholder="0"
          className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]"
        />
      </RequireReason>
      <button
        type="button"
        disabled={disabled}
        onClick={() => { onSubmit(asNum, reason.trim()); setN(""); setReason(""); }}
        data-testid="toolkit-cap-submit"
        className="w-full mt-3 px-4 py-2.5 text-[11px] uppercase tracking-widest font-bold border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
      >
        Review override
      </button>
    </div>
  );
}
